import { LoadingOutlined, SearchOutlined } from "@ant-design/icons"
import { Divider, Flex, type SelectProps, Typography } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { useEffect, useState } from "react"

import SelectComponent from "../../../../../components/Select"
import useDebounce from "../../../../../hooks/use-debounce"
import { showErrorNotifications } from "../../../../../utils/handlers/handle-error"
import { searchLeads } from "../../../api/services.ts"

const PAGE_SIZE = 30

export const CalendarAddLeadsSelect = React.forwardRef<
  HTMLSelectElement,
  SelectProps
>(({ onChange, value, ...props }, ref) => {
  const [search, setSearch] = useState<string>("")
  const [items, setItems] = useState<BaseOptionType[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [total, setTotal] = useState<number>(0)

  // Callers (e.g. a locked appointment lead) pass `{ value, label }` so the
  // caller doesn't need its own lookup, but the underlying Select's options
  // are keyed by raw id — an object value never matches one of those, so
  // antd falls back to rendering the raw id. Resolving both here keeps the
  // Select fed a plain id while still recovering the label via `fallbackLabels`.
  const isLabeledValue = !!value && typeof value === "object"
  const selectedId = isLabeledValue
    ? (value as BaseOptionType).value
    : (value as string | undefined)
  const selectedLabel = isLabeledValue
    ? (value as BaseOptionType).label
    : undefined

  const debounced = useDebounce(search, 500)

  const onSearchDependenciesHandler = async () => {
    try {
      setLoading(true)

      const res = await searchLeads({
        page: 1,
        page_size: PAGE_SIZE,
        ...(debounced ? { q: debounced } : {})
      })

      if (!res?.ok) throw new Error(res?.data?.details?.message)

      const leads = res?.data?.results || []
      const total: number = res?.data?.count || 0

      setTotal(total)

      setItems(
        leads?.map((lead) => ({
          value: lead?.id,
          label: lead?.lead_name || "-"
        }))
      )
    } catch (e) {
      showErrorNotifications(e)
    } finally {
      setLoading(false)
    }
  }

  const onChangeHandler: SelectProps["onChange"] = (value: string) => {
    onChange?.(value)

    // if it is null, it means that user cleared the selection
    if (!value) {
      setSearch("")
    }
  }

  useEffect(() => {
    void onSearchDependenciesHandler()
  }, [debounced])

  return (
    <SelectComponent
      ref={ref}
      value={selectedId}
      fallbackLabels={
        selectedId != null && selectedLabel
          ? { [String(selectedId)]: String(selectedLabel) }
          : undefined
      }
      showSearch
      onSearch={setSearch}
      onChange={onChangeHandler}
      onOpenChange={(v) => v && onSearchDependenciesHandler()}
      filterOption={false}
      loading={loading}
      size="large"
      optionLabelProp="label"
      placeholder="Search by name"
      options={items}
      suffixIcon={loading ? <LoadingOutlined /> : <SearchOutlined />}
      maxTagCount={"responsive"}
      notFoundContent={loading ? "Searching..." : "No leads found"}
      dropdownRender={(menu) => (
        <>
          {menu}

          {!!items?.length && (
            <>
              <Divider size={"small"} />

              <Flex className={"p-2"} gap={"small"} justify={"center"}>
                {loading ? (
                  <>
                    <Typography.Text>Searching...</Typography.Text>
                    <LoadingOutlined />
                  </>
                ) : (
                  <Typography.Text>
                    {total} lead{total > 1 ? "s" : ""} found — start typing to
                    filter them
                  </Typography.Text>
                )}
              </Flex>
            </>
          )}
        </>
      )}
      {...props}
    />
  )
})

CalendarAddLeadsSelect.displayName = "CalendarAddLeadsSelect"
